// กฎของงาน: เงื่อนไขปิดงาน, ประกัน, เครื่องค้างร้าน
import { addBangkokDays, bangkokDate, bangkokDaysBetween, fromBangkok } from './time.js';
/** สิ่งที่ยังขาดก่อนปิดงาน — ว่าง = ปิดได้ */
export function closeRequirements(ctx) {
    const out = [];
    for (const name of ctx.incompleteMandatoryChecklists) {
        out.push({ code: 'checklist', message: `กรอกเช็กลิสต์ "${name}" ให้ครบ` });
    }
    if (!ctx.hasSignature && !ctx.signatureWaivedReason?.trim()) {
        out.push({ code: 'signature', message: 'ให้ลูกค้าเซ็นรับงาน หรือระบุเหตุผลที่ไม่มีลายเซ็น' });
    }
    if (!ctx.resolution?.trim()) {
        out.push({ code: 'resolution', message: 'บันทึก "วิธีแก้ / สิ่งที่ทำ" ก่อนปิดงาน' });
    }
    if (!ctx.outcome) {
        out.push({ code: 'outcome', message: 'เลือกผลงาน: ซ่อมเสร็จ หรือ ซ่อมไม่ได้' });
    }
    if (ctx.linesWithoutPrice > 0) {
        out.push({ code: 'line_price', message: `มีรายการ ${ctx.linesWithoutPrice} บรรทัดที่ยังไม่ใส่ราคา` });
    }
    if (ctx.evReport?.required && ctx.evReport.status !== 'completed') {
        out.push({ code: 'ev_report', message: EV_REPORT_CLOSE_MESSAGE });
    }
    return out;
}
export const EV_REPORT_CLOSE_MESSAGE = "ออกรายงาน EV ให้เรียบร้อย (สถานะ 'ส่งค่ายรถแล้ว') ก่อนปิดงาน";
/** ค่าเริ่มต้นของการตั้งค่า `evReport` (แถวในตาราง settings ยังไม่มี = บังคับ) */
export const EV_REPORT_SETTING_DEFAULT = { requireCompletedToClose: true };
/**
 * งานนี้ต้องมีรายงาน EV สถานะ "ส่งค่ายรถแล้ว" ก่อนปิดไหม
 * - งานแบบ EV_CHARGER หรือ (หมวดติดตั้ง + มีข้อมูลติดตั้ง EV ที่เลือกค่ายรถแล้ว)
 * - และการตั้งค่า evReport.requireCompletedToClose เป็นจริง (ไม่มีแถวตั้งค่า = จริง)
 */
export function evReportRequired(job, setting) {
    const on = setting?.requireCompletedToClose ?? EV_REPORT_SETTING_DEFAULT.requireCompletedToClose;
    if (!on)
        return false;
    return job.jobTypeCode === 'EV_CHARGER' || (job.category === 'installation' && !!job.evTemplate);
}
// ---------- งานเสร็จ → รอแอดมิน → ปิดงาน (docs/DESIGN.md หัวข้อ 3.1)
export const BILLING_TYPES = ['charge', 'warranty', 'contract', 'internal'];
export const BILLING_TYPE_INFO = {
    charge: { label: 'คิดเงิน', hint: 'ต้องเสนอราคาหรือเก็บเงินลูกค้า' },
    warranty: { label: 'ในประกัน', hint: 'อยู่ในประกัน ไม่เก็บเงิน (ต้องมีเลขอ้างอิงประกัน)' },
    contract: { label: 'ตามสัญญา', hint: 'อยู่ในสัญญา MA/สัญญาดูแล (ต้องมีเลขสัญญา)' },
    internal: { label: 'งานภายใน', hint: 'งานของบริษัทเอง ไม่เก็บเงินลูกค้า' },
};
export function isBillingType(v) {
    return typeof v === 'string' && BILLING_TYPES.includes(v);
}
/** ประเภทที่ยืนยัน "ไม่คิดเงิน" ได้ */
export const NO_CHARGE_TYPES = ['warranty', 'contract', 'internal'];
/** การคิดเงินงานตามสัญญา (เช่น OMODA): require_ref = ต้องมีเลขสัญญา + แอดมินยืนยันไม่คิดเงิน · charge = ถือเป็นงานคิดเงิน ต้องมีเลขเอกสาร */
export const CONTRACT_BILLING_MODES = ['require_ref', 'charge'];
/** สิ่งที่ช่างต้องกรอกเพิ่มตอนกด "งานเสร็จ ส่งให้แอดมิน" (นอกจากเงื่อนไขปิดงานเดิม) */
export function workDoneRequirements(ctx) {
    const out = [];
    if (!isBillingType(ctx.billingType))
        out.push({ code: 'billing_type', message: 'เลือกประเภทการคิดเงิน: คิดเงิน / ในประกัน / ตามสัญญา / งานภายใน' });
    if (ctx.requirePhoto && ctx.photoCount < 1)
        out.push({ code: 'photo', message: 'แนบรูปผลงานอย่างน้อย 1 รูป' });
    return out;
}
/** ปิดงานจาก "รอแอดมิน" ได้เมื่อมีเลขเอกสาร หรือมีผู้มีสิทธิ์ยืนยันไม่คิดเงินแล้ว */
export function billingCloseRequirement(ctx) {
    if (ctx.billingRef?.trim() || ctx.peakInvoiceNo?.trim() || ctx.noChargeApprovedAt)
        return null;
    return { code: 'billing', message: 'ใส่เลขเอกสาร PEAK (ใบเสนอราคา/ใบแจ้งหนี้) หรือให้ผู้มีสิทธิ์ยืนยัน "ไม่คิดเงิน" ก่อนปิดงาน' };
}
/** เลขเอกสารที่แอดมินพิมพ์เอง — ไม่ตรวจกับ PEAK (กฎ token เดียว) แค่ตรวจรูปแบบ */
export const BILLING_DOC_RE = /^[A-Za-z0-9][A-Za-z0-9\-_\/.]{2,49}$/;
/** SETQ-/QT… = ใบเสนอราคา → quoted · อย่างอื่น (IV…) = ใบแจ้งหนี้ → billed */
export function billingDocKind(docNo) {
    return /^(SETQ|QT|QO)/i.test(docNo.trim()) ? 'quotation' : 'invoice';
}
export const ADMIN_NEXT_ACTION_INFO = {
    classify: { label: 'ระบุประเภทการคิดเงิน' },
    attach_doc: { label: 'ออกใบเสนอราคา/ใบแจ้งหนี้ใน PEAK แล้วใส่เลขเอกสาร' },
    approve_no_charge: { label: 'ตรวจเลขประกัน/สัญญา แล้วยืนยันไม่คิดเงิน' },
    close: { label: 'พร้อมปิดงาน' },
};
/** ขั้นถัดไปที่ระบบแนะนำในคิวรอแอดมิน */
export function adminNextAction(ctx) {
    if (!billingCloseRequirement(ctx))
        return 'close';
    if (!isBillingType(ctx.billingType))
        return 'classify';
    if (ctx.billingType === 'charge')
        return 'attach_doc';
    if (ctx.billingType === 'contract' && ctx.contractBillingMode === 'charge')
        return 'attach_doc';
    return 'approve_no_charge';
}
export function adminQueueAge(hours) {
    if (hours < 24)
        return 'fresh';
    if (hours <= 72)
        return 'aging';
    return 'overdue';
}
/**
 * เปิด/ยืนยันงานของแบบงานนี้ได้ไหม (เช่น งาน EV เปิดได้เฉพาะแอดมินที่รับผิดชอบ)
 * รายชื่อว่าง = ไม่จำกัด · admin ได้เสมอ · ระบบอัตโนมัติ (สัญญา MA/มอนิเตอร์) ได้ · API key และคนอื่นไม่ได้
 */
export function canCreateJobOfType(jobType, actor) {
    const list = jobType.allowedCreatorIds ?? [];
    if (list.length === 0)
        return true;
    if (actor.kind === 'system')
        return true;
    if (actor.kind !== 'user')
        return false;
    if (actor.role === 'admin')
        return true;
    return !!actor.userId && list.includes(actor.userId);
}
export function restrictedJobTypeMessage(jobTypeName) {
    return `งานประเภท "${jobTypeName}" เปิดได้เฉพาะผู้ที่ได้รับสิทธิ์ (แอดมินที่รับผิดชอบ) เท่านั้น`;
}
/** วันหมดประกันงานซ่อม (นับจากวันส่งมอบตามปฏิทินไทย) */
export function warrantyExpiry(deliveredAt, days) {
    if (!days || days <= 0)
        return null;
    return addBangkokDays(bangkokDate(deliveredAt), days);
}
export function isUnderWarranty(expiry, now) {
    if (!expiry)
        return false;
    return fromBangkok(expiry).getTime() + 24 * 60 * 60 * 1000 > now.getTime();
}
/** วันเตือนเครื่องค้างร้าน (นับจากวันที่งานเสร็จรอส่งมอบ) */
export const UNCLAIMED_REMINDER_DAYS = [7, 30, 45];
/** ครบกำหนดตามเงื่อนไขใบรับเครื่อง — ต้องให้ทนายตรวจถ้อยคำ (ดู DESIGN.md) */
export const UNCLAIMED_DISPOSAL_DAYS = 45;
/** เตือนรอบไหนที่ถึงกำหนดแล้วแต่ยังไม่ได้ส่ง */
export function dueUnclaimedReminders(readyAt, now, alreadySent) {
    const age = bangkokDaysBetween(readyAt, now);
    return UNCLAIMED_REMINDER_DAYS.filter((d) => age >= d && !alreadySent.includes(d));
}
//# sourceMappingURL=job-rules.js.map